import { ReactNode, useState, MouseEvent } from "react";
import { ArrowRightIcon } from "@common/components/icons";
import classNames from "classnames";
import useLogoutUserMutation from "@modules/user/hooks/useLogoutUserMutation";
import { useRouter } from "next/router";
import SideBar from "@layouts/profile/SideBar";
import { useTranslation, Trans } from "next-i18next";
import ActiveLink from "@common/components/ActiveLink";
import useLocalStorage from "@common/hooks/useLocalStorage";
import useUser from "@modules/user/hooks/useUser";
import Button from "@common/components/Button";
import {
  CreditAppModalMode,
  introductionMode,
} from "@modules/credit-app/types/CreditAppModalMode";
import CreditAppModal from "@modules/credit-app/components/CreditAppModal";
import DealTypeEnum from "@common/enums/DealTypeEnum";
import TradeInSourceName from "@modules/trade/enums/TradeInSourceName";
import useTradeInDetails from "@modules/trade/hooks/useTradeInDetails";
import getUserDealIdsByType from "@modules/purchase/helpers/getUserDealIdsByType";

type Props = {
  children: ReactNode;
  onHideMenuClick?(): void;
  showSideMenu: boolean;
  background?: boolean;
  unAuthenticatedMenu?: ReactNode;
};

const ProfileLayout = ({
  children,
  onHideMenuClick,
  showSideMenu,
  background = false,
  unAuthenticatedMenu,
}: Props) => {
  const logoutMutation = useLogoutUserMutation();
  const router = useRouter();
  const { user } = useUser();
  const { t } = useTranslation(["common", "profile", "tradeInPage"]);
  const [, setFirstName] = useLocalStorage<string | null>("firstName");
  // wait for redirect to initiate before log out request to prevent uneccessary requests for user data
  const logOut = async (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    setFirstName(null);
    await router.push("/");
    logoutMutation.mutate(undefined);
  };

  const [showCreditAppModal, setShowCreditAppModal] = useState(false);
  const [creditAppModalModeState, setCreditAppModalModeState] =
    useState<CreditAppModalMode>(introductionMode);
  const [creditIdState, setCreditIdState] = useState<string>("");

  const handleCreditAppModalClose = () => {
    setCreditAppModalModeState(introductionMode);
    setCreditIdState("");
    setShowCreditAppModal(false);
  };

  const { tradeInData } = useTradeInDetails(TradeInSourceName.RightRide);

  const icos = getUserDealIdsByType(user, DealTypeEnum.BuyIn);
  const aftermarketDeals = getUserDealIdsByType(user, DealTypeEnum.Aftermarket);

  const c2cDeals = getUserDealIdsByType(user, DealTypeEnum.C2CFinancing);

  const navigation = [
    {
      name: t("common:details"),
      href: "/profile",
      description: t("profile:details_side_nav_description"),
    },
    ...(!tradeInData?.noExistingTradeIn
      ? [
          {
            name: t("common:my_trade"),
            href: "/profile/my-trade",
            description: t("profile:my_trade_side_nav_description"),
          },
        ]
      : []),
    ...(aftermarketDeals && aftermarketDeals.length > 0
      ? [
          {
            name: t("common:warranty_protection"),
            href: "/profile/warranty-protection",
            description: t("profile:warranty_protection_side_nav_description"),
          },
        ]
      : []),
    {
      name: t("common:saved_searches"),
      href: "/profile/saved-searches",
      description: t("profile:saved_searches_side_nav_description"),
    },
    ...(c2cDeals && c2cDeals.length > 0
      ? [
          {
            name: t("profile:loan_applications_title"),
            href: "/profile/loan-applications",
            description: t("profile:saved_vehicles_side_nav_description"),
          },
        ]
      : []),
    {
      name: t("common:saved_vehicles"),
      href: "/profile/saved-vehicles",
      description: t("profile:saved_vehicles_side_nav_description"),
    },
    ...(icos && icos.length > 0
      ? [
          {
            name: t("common:instant_cash_offer"),
            href: "/profile/my-offers",
            description: t("profile:saved_vehicles_side_nav_description"),
          },
        ]
      : []),
  ];

  const unAuthNavigation = [
    {
      name: t("common:my_trade"),
      href: "/profile/my-trade",
      description: t("profile:my_trade_side_nav_description"),
    },
    {
      name: t("common:saved_vehicles"),
      href: "/profile/saved-vehicles",
      description: t("profile:saved_vehicles_side_nav_description"),
    },
    ...(aftermarketDeals && aftermarketDeals.length > 0
      ? [
          {
            name: t("common:warranty_protection"),
            href: "/profile/warranty-protection",
            description: t("profile:warranty_protection_side_nav_description"),
          },
        ]
      : []),
  ];

  const filteredUnAuthNavigation = unAuthNavigation;
  const filteredNavigation = navigation;

  const getFinancingElement = () => {
    return (
      <div className="p-4">
        <div className="text-center flex flex-col items-center rounded-2xl mt-4 p-4 bg-primary-light-500">
          <div className="text-2xl font-semibold">
            <Trans
              t={t}
              i18nKey="profile:shop_with_right_payment"
              components={{
                1: <span className="text-primary-bold" />,
              }}
            />
          </div>
          <div className="mt-1 font-medium">
            {t("profile:shop_with_right_payment_description")}
          </div>
          <div className="mt-2">
            <Button
              buttonStyle="primary"
              fill="filled"
              spacing="tight-hug"
              className="border border-gray-50"
              rightIcon={<ArrowRightIcon />}
              onClick={() => {
                setShowCreditAppModal(true);
              }}
            >
              {t("common:get_started")}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex mx-auto py-2.5 lg:pl-0 lg:pr-8">
        {user?.isLoggedIn && (
          <>
            <div
              className={classNames(
                "w-full sticky top-0 left-0 lg:flex lg:w-1/2 2xl:w-1/4 pr-6",
                {
                  inline: showSideMenu,
                  hidden: !showSideMenu,
                }
              )}
            >
              {onHideMenuClick && (
                <SideBar onHideClick={undefined} showSideBar>
                  <div className="divide-y divide-gray-100">
                    <div className="pb-6 px-6 flex flex-row justify-between">
                      <h1 className="text-text-light-200 body-4 font-semibold tracking-widest">
                        {t("common:my_profile").toUpperCase()}
                      </h1>
                    </div>
                    {filteredNavigation.map((item) => (
                      <ActiveLink
                        activeClassName="bg-white !bg-opacity-10"
                        href={item.href || "#"}
                        key={item.name}
                      >
                        {(active) => {
                          return (
                            <a
                              key={item.name}
                              href=""
                              onClick={() => onHideMenuClick()}
                              className="body-3 lg:mb-0 lg:pl-0 flex flex-row mb-2 p-4 bg-background-light-100 group lg:p-4 lg:items-center"
                            >
                              <div className="flex flex-col">
                                <div>
                                  {active ? (
                                    <span className="text-2xl align-bottom leading-5 hidden lg:inline lg:px-2 text-secondary-salmon">
                                      •
                                    </span>
                                  ) : (
                                    <span className="hidden lg:inline lg:px-3" />
                                  )}
                                  <h5
                                    className={classNames(
                                      "inline-block text-primary-deep body-3",
                                      {
                                        "lg:text-primary-bold lg:font-semibold":
                                          active,
                                      }
                                    )}
                                  >
                                    {item.name}
                                  </h5>
                                </div>

                                <div className="lg:hidden caption-2 text-gray-500">
                                  {item.description}
                                </div>
                              </div>
                              <div className="lg:hidden p-2 mt-auto mb-auto ml-auto">
                                <ArrowRightIcon className="text-primary-bright h-5 w-5" />
                              </div>
                            </a>
                          );
                        }}
                      </ActiveLink>
                    ))}
                    <a
                      href="#"
                      onClick={logOut}
                      className="body-3 lg:mb-0 lg:pl-0 flex flex-row mb-2 p-4 bg-background-light-100 group lg:p-4 lg:items-center"
                    >
                      <span className="hidden lg:inline lg:px-3" />
                      <h5 className="inline-block text-primary-deep body-3">
                        {t("common:logout")}
                      </h5>
                    </a>
                    {getFinancingElement()}
                  </div>
                </SideBar>
              )}
            </div>
            <div
              className={classNames("w-full lg:inline", {
                inline: !showSideMenu,
                hidden: showSideMenu,
                "bg-no-repeat bg-contain bg-right-top": background,
              })}
            >
              {children}
            </div>
          </>
        )}
        {!user?.isLoggedIn && (
          <>
            <div
              className={classNames(
                "w-full sticky top-0 left-0 lg:flex lg:w-1/2 2xl:w-1/4 pr-6",
                {
                  inline: showSideMenu,
                  hidden: !showSideMenu,
                }
              )}
            >
              {onHideMenuClick && (
                <SideBar onHideClick={undefined} showSideBar>
                  <div className="divide-y divide-gray-100 h-screen">
                    {showSideMenu && (
                      <div className="pb-6 px-6 flex flex-row justify-between lg:hidden">
                        <h1 className="text-text-light-200 body-4 font-semibold tracking-widest">
                          {t("common:my_profile").toUpperCase()}
                        </h1>
                      </div>
                    )}
                    <div className="hidden lg:block">{unAuthenticatedMenu}</div>
                    {filteredUnAuthNavigation.map((item) => (
                      <ActiveLink
                        activeClassName="bg-white !bg-opacity-10"
                        href={item.href || "#"}
                        key={item.name}
                      >
                        {(active) => {
                          return (
                            <a
                              key={item.name}
                              href=""
                              onClick={() => onHideMenuClick()}
                              className="body-3 lg:mb-0 lg:pl-0 flex flex-row mb-2 p-4 bg-background-light-100 group lg:p-4 lg:items-center"
                            >
                              <div className="flex flex-col">
                                <div>
                                  {active ? (
                                    <span className="text-2xl align-bottom leading-5 hidden lg:inline lg:px-2 text-secondary-salmon">
                                      •
                                    </span>
                                  ) : (
                                    <span className="hidden lg:inline lg:px-3" />
                                  )}
                                  <h5
                                    className={classNames(
                                      "inline-block text-primary-deep body-3",
                                      {
                                        "lg:text-primary-bold lg:font-semibold":
                                          active,
                                      }
                                    )}
                                  >
                                    {item.name}
                                  </h5>
                                </div>

                                <div className="lg:hidden caption-2 text-gray-500">
                                  {item.description}
                                </div>
                              </div>
                              <div className="lg:hidden p-2 mt-auto mb-auto ml-auto">
                                <ArrowRightIcon className="text-primary-bright h-5 w-5" />
                              </div>
                            </a>
                          );
                        }}
                      </ActiveLink>
                    ))}
                    {getFinancingElement()}
                  </div>
                </SideBar>
              )}
            </div>
            <div
              className={classNames("w-full lg:inline", {
                inline: !showSideMenu,
                hidden: showSideMenu,
                "bg-no-repeat bg-contain bg-right-top": background,
              })}
            >
              {children}
            </div>
          </>
        )}
      </div>
      {showCreditAppModal && (
        <CreditAppModal
          modeState={[creditAppModalModeState, setCreditAppModalModeState]}
          creditAppIdState={[creditIdState, setCreditIdState]}
          showModal={showCreditAppModal}
          handleModalClose={handleCreditAppModalClose}
        />
      )}
    </>
  );
};

export default ProfileLayout;
